/* Copyright (c) 2023-2026 Nordcloud Oy or its affiliates. All Rights Reserved. */

import styled, { css, keyframes } from "styled-components";
import { Box, FlexContainer, theme } from "@nordcloud/gnui";

export const DASHBOARD_MOBILE_BREAKPOINT = theme.breakpoints.lg;

export const ChildrenWrap = styled.div`
  flex: 1;
  align-self: center;
`;

export const StatusBarWrap = styled(FlexContainer)`
  width: 100%;
  height: ${theme.lineHeight};
  border-radius: 0.25rem;
  overflow: hidden;
`;

export const TopWidgetCardWrap = styled(Box)`
  flex: 1 1 0;
  flex-direction: row;
  min-width: 440px;
  display: flex;
  gap: ${theme.spacing.spacing07};

  @media (max-width: ${DASHBOARD_MOBILE_BREAKPOINT}px) {
    flex-direction: column;
    gap: ${theme.spacing.spacing03};
    min-width: auto;
  }
`;

type StatusBarItemProps = {
  width: string;
  background: string;
  hasAnimation?: boolean;
};

const expandWidth = keyframes`
  from {
    max-width: 0;
  }
  to {
    max-width: 1000px;
  }
`;

export const StatusBarItem = styled.div<StatusBarItemProps>`
  height: 100%;
  width: ${({ width }) => width};
  background: ${({ background }) => background};
  transform-origin: left;
  animation: ${({ hasAnimation }) =>
    hasAnimation
      ? css`
          ${expandWidth} 1s ease-in-out
        `
      : "none"};
`;

export const LegendItemWrap = styled(FlexContainer)<{
  widthCh?: number;
  fontResponsivePoint?: number;
}>`
  gap: ${theme.spacing.spacing02};
  margin-right: ${theme.spacing.spacing04};
  white-space: nowrap;
  font-size: ${theme.fontSizes.md};
  width: ${({ widthCh }) => (widthCh ? `${widthCh}ch` : "auto")};

  a,
  p {
    white-space: nowrap;
  }

  .is-sm-visible {
    display: none;
  }

  ${({ fontResponsivePoint }) =>
    fontResponsivePoint !== undefined &&
    css`
      @media (max-width: ${fontResponsivePoint}px) {
        font-size: ${theme.fontSizes.sm};

        .is-sm-visible {
          font-size: ${theme.fontSizes.sm};
          display: inline-block;
        }

        .is-md-visible {
          display: none;
        }
      }
    `}
`;

type ValueTagProps = {
  color: string;
};

export const ValueTag = styled.span<ValueTagProps>`
  box-sizing: border-box;
  display: inline-block;
  min-width: 42px;
  min-height: 1.75em;
  line-height: 1.75;
  font-size: ${theme.fontSizes.sm};
  color: ${theme.color.text.text04};
  background-color: ${({ color }) => color};
  text-align: center;
  border-radius: ${theme.radius.sm};
`;

export const ColorBadge = styled.span<ValueTagProps>`
  display: inline-block;
  width: ${theme.fontSizes.sm};
  height: ${theme.fontSizes.sm};
  background-color: ${({ color }) => color};
  border-radius: ${theme.radius.sm};
`;

export const DoughnutChartTextWrap = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  text-align: center;
`;
